import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { useTheme } from './ThemeProvider';
import discordIcon from '../assets/discord_icon.png';

const DISCORD_INVITE_URL = 'https://discord.gg/3TPhjKxQ93';
const BUG_REPORT_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSeV9tV5QsSSS-59GuR5_TDUu_KAiYzf-u0PuV0jixgrYv3QBA/viewform?usp=dialog';

interface SupportLinksProps {
  visible: boolean;
}

/**
 * The Discord and Report Bugs buttons.
 *
 * Lifted out of YapDayTimer, which is where they used to live purely because
 * that component was already portalled and pinned above the bottom nav. It also
 * carries the YapTab-only "create yap" button, so the two could not simply move
 * together — these are community/support links and belong on Explore, while
 * posting stays on the feed.
 *
 * Portalled and fixed-positioned for the same reason as before: the screen
 * underneath scrolls, and these should not scroll with it.
 */
export function SupportLinks({ visible }: SupportLinksProps) {
  const [mounted, setMounted] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Caption under each button. Absolutely positioned so a label wider than its
  // 44px button overhangs evenly instead of dragging the button off the margin.
  const captionStyle: React.CSSProperties = {
    position: 'absolute',
    top: '48px',
    left: '50%',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.2px',
    color: theme === 'dark' ? '#ffffff' : '#000000',
    // The screen scrolls underneath, so the caption needs its own contrast.
    textShadow: theme === 'dark'
      ? '0 1px 3px rgba(0,0,0,0.9)'
      : '0 1px 3px rgba(255,255,255,0.9)',
    pointerEvents: 'none',
  };

  // Measured from the nav's own safe-area padding so the clearance stays honest
  // on a device with a home indicator, where the nav is ~22px taller.
  const discordBottom = 'calc(max(env(safe-area-inset-bottom), 12px) + 92px)';
  const bugReportBottom = 'calc(max(env(safe-area-inset-bottom), 12px) + 160px)';

  if (!mounted || !visible) return null;

  return createPortal(
    <>
    {/* ── Bottom-LEFT, above Discord: bug report form.
        The wrapper stays exactly 44px wide so the caption underneath can't push
        this button out of line with the Discord one below it — the caption is
        absolutely positioned and allowed to overhang on both sides. */}
    <div
      style={{
        position: 'fixed',
        bottom: bugReportBottom,
        left: '16px',
        width: '44px',
        height: '44px',
        zIndex: 40,
      }}
    >
      <motion.a
        href={BUG_REPORT_FORM_URL}
        target="_blank"
        rel="noopener noreferrer"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Report a bug"
        className="w-11 h-11 aspect-square shrink-0 rounded-full backdrop-blur-md flex items-center justify-center pointer-events-auto"
        style={{
          background: '#0F9D58', // Google Sheets green, solid like the Discord blurple
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)',
          textDecoration: 'none',
        }}
      >
        {/* Sheets glyph inline rather than an asset: two colours, no network,
            and it stays crisp at any density. */}
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" style={{ display: 'block' }}>
          <path d="M6.5 2h8L19 6.5V21a1 1 0 0 1-1 1H6.5a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" fill="#ffffff" />
          <path d="M14.5 2 19 6.5h-4.5z" fill="#c6e7d6" />
          <path
            d="M8 10.6h8v7.4H8zM8 13.1h8M8 15.6h8M12 10.6v7.4"
            fill="none"
            stroke="#0F9D58"
            strokeWidth="1.2"
          />
        </svg>
      </motion.a>
      <span style={captionStyle}>Report Bugs</span>
    </div>

    {/* ── Bottom-LEFT: Discord invite button (same 44px round as the + button) */}
    <div
      style={{
        position: 'fixed',
        bottom: discordBottom,
        left: '16px',
        width: '44px',
        height: '44px',
        zIndex: 40,
      }}
    >
      <motion.a
        href={DISCORD_INVITE_URL}
        target="_blank"
        rel="noopener noreferrer"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Join our Discord server"
        className="w-11 h-11 aspect-square shrink-0 rounded-full backdrop-blur-md flex items-center justify-center pointer-events-auto"
        style={{
          background: '#5865F2', // Discord blurple — solid so the icon reads cleanly
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)',
          textDecoration: 'none',
        }}
      >
        <img
          src={discordIcon}
          alt=""
          style={{ width: '22px', height: '22px', objectFit: 'contain', display: 'block' }}
        />
      </motion.a>
      <span style={captionStyle}>Discord</span>
    </div>

    </>,
    document.body,
  );
}
